import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  IconButton,
  Switch,
  Tab,
  Tabs,
} from '@mui/material';
import {
  TuneRounded as TuneIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { CustomField, FormTheme, WorkflowSettings, WorkflowTemplate } from '../../types';
import { WorkflowSettingsTab } from './WorkflowSettingsTab';
import { CustomFieldsTab } from './CustomFieldsTab';
import {
  THEME_UI,
  ColorSection,
  LabeledText,
  SuccessPreview,
} from './themeEditorParts';

/* Defaults mirror ThemeEditor's DEFAULT_THEME entries for the same keys — the
   success copy is stored on the theme record, so both dialogs must fall back to
   identical text or the preview drifts from what the renderer paints. */
const DEFAULTS = {
  successTitle: 'Form Submitted Successfully!',
  successMessage: 'Thank you for your time. Your response has been recorded.',
  successButtonLabel: 'Okay',
  successDownloadLabel: 'Download brochure',
};

// Prefilled the moment the author flips the enable switch on with nothing
// entered yet — a working example beats a blank required-looking field, and
// they can still overwrite either before saving.
const DOWNLOAD_URL_PLACEHOLDER = 'https://example.com/brochure.pdf';

interface AdvancedSettingsModalProps {
  open: boolean;
  onClose: () => void;
  theme: FormTheme | null;
  /** Saves the settings; the dialog stays open if this rejects or the caller does not close it. */
  onSave: (theme: FormTheme) => void | Promise<void>;
  onChange?: (theme: FormTheme) => void;
  /** Workflow + custom field edits apply to the builder immediately and are saved with the form. */
  workflowSettings?: WorkflowSettings | null;
  onWorkflowSettingsChange: (settings: WorkflowSettings) => void;
  workflowTemplates: {
    templates: WorkflowTemplate[] | null;
    loading: boolean;
    error: boolean;
    reload: () => void;
  };
  /** Field names the form and its custom fields already provide. */
  presentFieldNames: Set<string>;
  /** Names of the form's own input fields (not custom fields). */
  formFieldNames: Set<string>;
  /** `field_name`s of the form's AI Generator fields (for workflow variables). */
  aiFieldNames: string[];
  submitEveryStep: boolean;
  customFields: CustomField[];
  onCustomFieldsChange: (fields: CustomField[]) => void;
}

export const AdvancedSettingsModal: React.FC<AdvancedSettingsModalProps> = ({
  open,
  onClose,
  theme,
  onSave,
  onChange,
  workflowSettings,
  onWorkflowSettingsChange,
  workflowTemplates,
  presentFieldNames,
  formFieldNames,
  aiFieldNames,
  submitEveryStep,
  customFields,
  onCustomFieldsChange,
}) => {
  const [formTheme, setFormTheme] = useState<FormTheme>(theme || {});
  const [applying, setApplying] = useState(false);
  const [tab, setTab] = useState<'success' | 'workflow' | 'custom'>('success');

  useEffect(() => {
    if (theme) setFormTheme(theme);
  }, [theme]);

  const handleChange = (field: keyof FormTheme, value: any) => {
    const updated = { ...formTheme, [field]: value };
    setFormTheme(updated);
    onChange?.(updated);
  };

  const downloadUrl = formTheme.successDownloadUrl ?? '';
  const downloadLabel = formTheme.successDownloadLabel ?? '';
  const downloadEnabled = Boolean(formTheme.successDownloadEnabled);

  const handleDownloadEnabledChange = (enabled: boolean) => {
    const updated: FormTheme = { ...formTheme, successDownloadEnabled: enabled };
    // Prefill only on the on-flip, and only into fields the author hasn't
    // already typed something into — never clobber an existing value, and
    // never re-seed on every render (that's why this lives in the click
    // handler, not a `downloadEnabled` effect).
    if (enabled) {
      if (!updated.successDownloadUrl?.trim()) {
        updated.successDownloadUrl = DOWNLOAD_URL_PLACEHOLDER;
      }
      if (!updated.successDownloadLabel?.trim()) {
        updated.successDownloadLabel = DEFAULTS.successDownloadLabel;
      }
    }
    setFormTheme(updated);
    onChange?.(updated);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2.5,
            overflow: 'hidden',
            width: '100%',
            maxWidth: 1020,
            height: '88vh',
            maxHeight: 760,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 24px 64px rgba(16,24,40,0.22)',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.75,
          borderBottom: '1px solid',
          borderColor: THEME_UI.divider,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: THEME_UI.accentSoft,
              color: THEME_UI.accent,
            }}
          >
            <TuneIcon sx={{ fontSize: 19 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 15, lineHeight: 1.25 }}>
              Advanced Settings
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              What happens after someone submits this form
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            width: 32,
            height: 32,
            color: 'text.secondary',
            '&:hover': { bgcolor: THEME_UI.hover, color: 'text.primary' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, next) => setTab(next)}
        sx={{ px: 2.5, borderBottom: '1px solid', borderColor: THEME_UI.divider, minHeight: 44 }}
      >
        <Tab value="success" label="Success page" sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }} />
        <Tab value="workflow" label="Workflow" sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }} />
        <Tab
          value="custom"
          label={customFields.length ? `Custom fields (${customFields.length})` : 'Custom fields'}
          sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }}
        />
      </Tabs>

      <DialogContent sx={{ p: 0, flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {tab === 'workflow' && (
          <Box sx={{ p: 2.5, height: '100%', overflowY: 'auto', bgcolor: THEME_UI.fieldBg }}>
            <WorkflowSettingsTab
              settings={workflowSettings}
              onChange={onWorkflowSettingsChange}
              templates={workflowTemplates.templates}
              loading={workflowTemplates.loading}
              error={workflowTemplates.error}
              onReload={workflowTemplates.reload}
              presentNames={presentFieldNames}
              aiFieldNames={aiFieldNames}
              submitEveryStep={submitEveryStep}
            />
          </Box>
        )}
        {tab === 'custom' && (
          <Box sx={{ p: 2.5, height: '100%', overflowY: 'auto', bgcolor: THEME_UI.fieldBg }}>
            <CustomFieldsTab
              customFields={customFields}
              onChange={onCustomFieldsChange}
              formFieldNames={formFieldNames}
            />
          </Box>
        )}
        {tab === 'success' && (
        <Grid container sx={{ height: '100%', flexWrap: { md: 'nowrap' } }}>
          <Grid
            size={{ xs: 12, md: 7 }}
            sx={{
              p: 2.5,
              height: '100%',
              overflowY: 'auto',
              backgroundColor: THEME_UI.sectionBg,
              borderRight: { md: '1px solid' },
              borderColor: { md: THEME_UI.divider },
              '&::-webkit-scrollbar': { width: 8 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: THEME_UI.scrollbar,
                borderRadius: 8,
                border: '2px solid transparent',
                backgroundClip: 'content-box',
              },
              scrollbarWidth: 'thin',
            }}
          >
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
              Shown to users after they submit the form.
            </Typography>

            <ColorSection title="Title" description="Heading shown at the top.">
              <LabeledText
                value={formTheme.successTitle ?? DEFAULTS.successTitle}
                onChange={(value) => handleChange('successTitle', value)}
              />
            </ColorSection>

            <ColorSection title="Message" description="Body copy below the title.">
              <LabeledText
                multiline
                minRows={2}
                value={formTheme.successMessage ?? DEFAULTS.successMessage}
                onChange={(value) => handleChange('successMessage', value)}
              />
            </ColorSection>

            <ColorSection title="Button" description="The closing action button.">
              <LabeledText
                value={formTheme.successButtonLabel ?? DEFAULTS.successButtonLabel}
                onChange={(value) => handleChange('successButtonLabel', value)}
              />
            </ColorSection>

            <ColorSection
              title="Download after submit"
              description="Paste a link to a hosted file (brochure, price list, ticket) and the success screen shows a download button."
              headerAction={
                <Switch
                  checked={downloadEnabled}
                  onChange={(e) => handleDownloadEnabledChange(e.target.checked)}
                />
              }
            >
              <LabeledText
                label="File URL"
                placeholder={DOWNLOAD_URL_PLACEHOLDER}
                value={downloadUrl}
                onChange={(value) => handleChange('successDownloadUrl', value)}
                disabled={!downloadEnabled}
              />
              <LabeledText
                label="Download button label"
                placeholder={DEFAULTS.successDownloadLabel}
                value={downloadLabel}
                onChange={(value) => handleChange('successDownloadLabel', value)}
                disabled={!downloadEnabled}
              />
            </ColorSection>

            {/* Auto-download sits outside ColorSection's grid: it is a single
                row control, and the grid's 200px columns would strand the
                switch away from its own label. */}
            {downloadEnabled && downloadUrl.trim() !== '' && (
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  border: '1px solid',
                  borderColor: THEME_UI.border,
                  borderRadius: 2.5,
                  backgroundColor: THEME_UI.fieldBg,
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '13.5px', color: THEME_UI.text }}>
                    Start the download automatically
                  </Typography>
                  <Typography
                    sx={{ fontSize: '11.5px', lineHeight: 1.5, color: 'text.secondary', mt: 0.25 }}
                  >
                    The file downloads as soon as the success screen appears. The button stays
                    visible either way, so the user can fetch it again.
                  </Typography>
                </Box>
                <Switch
                  checked={Boolean(formTheme.successDownloadAuto)}
                  onChange={(e) => handleChange('successDownloadAuto', e.target.checked)}
                />
              </Box>
            )}
          </Grid>

          <Grid
            size={{ xs: 12, md: 5 }}
            sx={{ p: 2.5, height: '100%', overflowY: 'auto', bgcolor: THEME_UI.fieldBg }}
          >
            <SuccessPreview
              title={formTheme.successTitle ?? DEFAULTS.successTitle}
              message={formTheme.successMessage ?? DEFAULTS.successMessage}
              buttonLabel={formTheme.successButtonLabel ?? DEFAULTS.successButtonLabel}
              downloadLabel={
                downloadEnabled && downloadUrl.trim()
                  ? downloadLabel.trim() || DEFAULTS.successDownloadLabel
                  : undefined
              }
            />
          </Grid>
        </Grid>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.75,
          borderTop: '1px solid',
          borderColor: THEME_UI.divider,
          backgroundColor: THEME_UI.sectionBg,
          justifyContent: 'flex-end',
          gap: 1,
        }}
      >
        <Button onClick={onClose} color="inherit" sx={{ textTransform: 'none', fontWeight: 600 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={applying}
          onClick={async () => {
            setApplying(true);
            try {
              await onSave(formTheme);
            } finally {
              setApplying(false);
            }
          }}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            backgroundColor: THEME_UI.accent,
            '&:hover': { backgroundColor: THEME_UI.accentHover },
          }}
        >
          {applying ? 'Saving…' : 'Apply Settings'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdvancedSettingsModal;
